import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import SystemAdminRoute from './components/SystemAdminRoute';
import StaffOnlyRoute from './components/StaffOnlyRoute';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import OfflineDetector from './components/OfflineDetector';
import RoleBasedRedirect from './components/RoleBasedRedirect';

// Pages
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Vehicles from './pages/Vehicles';
import VehicleForm from './pages/VehicleForm';
import VehicleStatusUpdate from './pages/VehicleStatusUpdate';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import VehicleCall from './pages/VehicleCall';
import NotFound from './pages/NotFound';

// Admin Pages
import SystemAdminDashboard from './pages/SystemAdminDashboard';
import LocationManagement from './pages/LocationManagement';
import SystemAdminLocationManagement from './pages/SystemAdminLocationManagement';
import UserManagement from './pages/UserManagement';
import SystemAdminReports from './pages/SystemAdminReports';
import NewLocationForm from './pages/NewLocationForm';
import EditLocationForm from './pages/EditLocationForm';
import NewPatronForm from './pages/NewPatronForm';
import NewUserForm from './pages/NewUserForm';
import EditUserForm from './pages/EditUserForm';
import AdminSettings from './pages/AdminSettings';
import ContactMessages from './pages/ContactMessages';
import DemoApplications from './pages/DemoApplications';
import AdminAdvertisements from './pages/AdminAdvertisements';
import AdminAdvertisementForm from './pages/AdminAdvertisementForm';
import AdminNotifications from './pages/AdminNotifications';
import AdminDevices from './pages/AdminDevices';
import APITokens from './pages/APITokens';

// Owner Pages
import OwnerDashboard from './pages/OwnerDashboard';
import OwnerUserManagement from './pages/OwnerUserManagement';
import OwnerNewUserForm from './pages/OwnerNewUserForm';
import OwnerEditUserForm from './pages/OwnerEditUserForm';
import OwnerSettings from './pages/OwnerSettings';
import OwnerReports from './pages/OwnerReports';
import OwnerVehicleDetails from './pages/OwnerVehicleDetails';
import OwnerLocationManagement from './pages/OwnerLocationManagement';
import OwnerEditLocationForm from './pages/OwnerEditLocationForm';

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <OfflineDetector />
        <Router future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/r-p" element={<ResetPassword />} />
            <Route path="/call/:qrToken" element={<VehicleCall />} />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<RoleBasedRedirect />} />

              {/* System Admin Routes */}
              <Route path="admin" element={<SystemAdminRoute><SystemAdminDashboard /></SystemAdminRoute>} />
              <Route path="admin/locations" element={<SystemAdminRoute><SystemAdminLocationManagement /></SystemAdminRoute>} />
              <Route path="admin/locations/new" element={<SystemAdminRoute><NewLocationForm /></SystemAdminRoute>} />
              <Route path="admin/locations/edit/:id" element={<SystemAdminRoute><EditLocationForm /></SystemAdminRoute>} />
              <Route path="admin/patrons/new" element={<SystemAdminRoute><NewPatronForm /></SystemAdminRoute>} />
              <Route path="admin/users" element={<SystemAdminRoute><UserManagement /></SystemAdminRoute>} />
              <Route path="admin/users/new" element={<SystemAdminRoute><NewPatronForm /></SystemAdminRoute>} />
              <Route path="admin/users/edit/:id" element={<SystemAdminRoute><EditUserForm /></SystemAdminRoute>} />
              <Route path="admin/contact-messages" element={<SystemAdminRoute><ContactMessages /></SystemAdminRoute>} />
              <Route path="admin/applications" element={<SystemAdminRoute><DemoApplications /></SystemAdminRoute>} />
              <Route path="admin/advertisements" element={<SystemAdminRoute><AdminAdvertisements /></SystemAdminRoute>} />
              <Route path="admin/advertisements/new" element={<SystemAdminRoute><AdminAdvertisementForm /></SystemAdminRoute>} />
              <Route path="admin/advertisements/edit/:id" element={<SystemAdminRoute><AdminAdvertisementForm /></SystemAdminRoute>} />
              <Route path="admin/notifications" element={<SystemAdminRoute><AdminNotifications /></SystemAdminRoute>} />
              <Route path="admin/devices" element={<SystemAdminRoute><AdminDevices /></SystemAdminRoute>} />
              <Route path="admin/reports" element={<SystemAdminRoute><SystemAdminReports /></SystemAdminRoute>} />
              <Route path="admin/api-tokens" element={<SystemAdminRoute><APITokens /></SystemAdminRoute>} />
              <Route path="admin/settings" element={<SystemAdminRoute><AdminSettings /></SystemAdminRoute>} />

              {/* Owner/Staff Routes */}
              <Route path="owner" element={<OwnerDashboard />} />
              <Route path="owner/users" element={<OwnerUserManagement />} />
              <Route path="owner/users/new" element={<OwnerNewUserForm />} />
              <Route path="owner/users/edit/:userId" element={<OwnerEditUserForm />} />
              <Route path="owner/locations" element={<OwnerLocationManagement />} />
              <Route path="owner/locations/edit/:id" element={<OwnerEditLocationForm />} />
              <Route path="owner/vehicles" element={<Vehicles />} />
              <Route path="owner/vehicles/details/:id" element={<OwnerVehicleDetails />} />
              <Route path="owner/reports" element={<OwnerReports />} />
              <Route path="owner/settings" element={<OwnerSettings />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="vehicles" element={<Vehicles />} />
              <Route path="vehicles/new" element={<StaffOnlyRoute><VehicleForm /></StaffOnlyRoute>} />
              <Route path="vehicles/status/:id" element={<StaffOnlyRoute><VehicleStatusUpdate /></StaffOnlyRoute>} />
              <Route path="vehicles/:locationId" element={<Vehicles />} />
              <Route path="staff/reports" element={<Reports />} />
              <Route path="staff/reports/:locationId" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            {/* 404 route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </AuthProvider>
    </div>
  );
}

export default App;